import type { ReactElement } from 'react';
import { useEffect, useState, useRef } from 'react';
import CartItem from 'models/CartItem';
import { useCart } from 'contexts/Cart';
import { toCurrency } from 'utils/numbers';

import { Component as CartIcon } from 'icons/icon-ui--cart-small.svg';
import { Component as CheckIcon } from 'icons/icon-ui--check.svg';
import useSaveCart from '../../../hooks/useSaveCart';
import useAppSync from '../../../hooks/useAppSync';

type Props = {
  selectedPackageProducts: CartItem[];
  allPackageProductsInCart: boolean;
  toggleSelected: (skuId: number) => void;
};

enum ButtonMode {
  DEFAULT = 'default',
  ADDING = 'adding',
  EMPTY = 'empty',
}

function ProductPackageDetails({
  selectedPackageProducts,
  allPackageProductsInCart,
  toggleSelected,
}: Props): ReactElement {
  const { dispatch, cart } = useCart();
  const saveCart = useSaveCart();
  const sync = useAppSync('CartSummaryApp', 'AddToCartApp', dispatch);
  const [buttonMode, setButtonMode] = useState(ButtonMode.DEFAULT);
  const [addedToCartCount, setAddedToCartCount] = useState(0);
  const packageProductMenuItems = cart.menu_items.filter((item) => {
    const hasMatchingSku = cart.package_products.some(
      (packageProduct) => packageProduct.sku_id === item.sku.id,
    );

    return hasMatchingSku;
  });
  const totalPrice = selectedPackageProducts.reduce((total, item) => {
    const priceFloat = parseFloat(item.sku.price);
    return total + parseFloat(priceFloat.toFixed(2));
  }, 0);

  const nonSelectedPackageProducts = selectedPackageProducts.filter(
    (item) => item.quantity === 0,
  );
  const packageProductsInCart = packageProductMenuItems.filter((item) => item.quantity > 0);
  const setQuantity = (qty: number, skuId: number) => {
    dispatch({ type: 'setMenuItemQuantity', quantity: qty, skuId });
    sync({ type: 'setMenuItemQuantity', quantity: qty, skuId });
    saveCart();
  };

  useEffect(() => {
    if (
      selectedPackageProducts.length === 0
      && buttonMode === ButtonMode.DEFAULT
    ) {
      setButtonMode(ButtonMode.EMPTY);
    }
    if (selectedPackageProducts.length > 0) {
      setButtonMode(ButtonMode.DEFAULT);
    }
  }, [selectedPackageProducts, buttonMode]);

  const prevCartLengthRef = useRef(packageProductsInCart.length);

  useEffect(() => {
    if (buttonMode === ButtonMode.ADDING) {
      if (packageProductsInCart.length < prevCartLengthRef.current) {
        setAddedToCartCount((prevAddedToCartCount) => prevAddedToCartCount - 1);
      }
      prevCartLengthRef.current = packageProductsInCart.length;
    }
  }, [buttonMode, packageProductsInCart.length, prevCartLengthRef]);

  useEffect(() => {
    if (buttonMode === ButtonMode.ADDING) {
      if (addedToCartCount === 0) {
        setButtonMode(ButtonMode.EMPTY);
      }
      saveCart();
    }
  }, [addedToCartCount]);

  const handleAddToCart = () => {
    if (selectedPackageProducts.length > 0) {
      setAddedToCartCount(selectedPackageProducts.length);
    }
    selectedPackageProducts.forEach((item) => {
      toggleSelected(item.sku.id);
      setQuantity(1, item.sku.id);
      setButtonMode(ButtonMode.ADDING);
    });
  };

  return (
    <div className="container d-flex justify-content-center pt-3 pb-3">
      <div className="row">
        <div
          className="col text-center d-flex justify-content-between"
        >
          <div
            className="row justify-content-center"
            style={{ gap: '1rem' }}
          >
            {buttonMode === ButtonMode.DEFAULT && (
              <h2
                className="col text-center"
                style={{ top: '.25rem' }}
              >
                {toCurrency(totalPrice)}
              </h2>
            )}
            <div className="col text-center">
              {buttonMode !== ButtonMode.EMPTY && (
                <button
                  type="button"
                  className="c-add-to-cart__default"
                  onClick={handleAddToCart}
                  style={{ width: '14rem' }}
                >
                  <>
                    {buttonMode === ButtonMode.ADDING ? (
                      <>
                        <CheckIcon className="c-svg-fill-white-color" width="12" />
                        <span className="c-add-to-cart__default-text">
                          {`${addedToCartCount} ${addedToCartCount > 1
                            ? 'items'
                            : 'item'
                          } added to cart!`}
                        </span>
                      </>
                    ) : (
                      <>
                        <CartIcon className="c-svg-stroke-white-color" width="16" />
                        <span className="c-add-to-cart__default-text">
                          {`Add ${
                            nonSelectedPackageProducts.length
                          } ${nonSelectedPackageProducts.length > 1
                            ? 'items'
                            : 'item'
                          } to cart`}
                        </span>
                      </>
                    )}
                  </>
                </button>
              )}
              {buttonMode === ButtonMode.EMPTY
                  && !allPackageProductsInCart && (
                  <span className="c-product-package-details__add-items-text">
                    Select an item to add
                  </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export default ProductPackageDetails;
